import React, { useState, useEffect, useCallback } from 'react';
import { encountersAPI } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { generateEncounterReport, generateMedicalCertificate, generateReferralLetter } from '../lib/pdfGenerator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Stethoscope, FileText, Trash2, Edit2, ChevronDown, ChevronRight,
  Loader2, Heart, Thermometer, Activity, Clock, Download, AlertTriangle,
  ClipboardList, Send, Calendar, User
} from 'lucide-react';

const ENCOUNTER_TYPES = [
  { value: 'consultation', label: 'Consultation' },
  { value: 'follow_up', label: 'Follow-Up' },
  { value: 'urgent', label: 'Urgent Visit' },
  { value: 'procedure', label: 'Procedure' },
  { value: 'telehealth', label: 'Telehealth' },
];

const ENCOUNTER_TYPE_COLORS = {
  consultation: 'bg-emerald-100 text-emerald-700',
  follow_up: 'bg-blue-100 text-blue-700',
  urgent: 'bg-red-100 text-red-700',
  procedure: 'bg-purple-100 text-purple-700',
  telehealth: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS = {
  draft: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  signed: 'bg-blue-100 text-blue-700',
};

const emptyVitals = {
  blood_pressure_systolic: '', blood_pressure_diastolic: '',
  heart_rate: '', temperature: '', respiratory_rate: '',
  oxygen_saturation: '', weight: '', height: '', bmi: '',
};

const emptyForm = {
  encounter_type: 'consultation',
  chief_complaint: '',
  vital_signs: { ...emptyVitals },
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  physical_exam: '',
  diagnoses: [{ code: '', description: '', type: 'primary' }],
  management_plan: '',
  follow_up: '',
  status: 'completed',
};

export default function PatientEncountersTab({ patientId, patient }) {
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docEncounter, setDocEncounter] = useState(null);
  const [docType, setDocType] = useState('medical_report');
  const [docFields, setDocFields] = useState({ additional_notes: '', start_date: '', end_date: '', certificate_reason: '', referred_to: '', specialty: '', urgency: 'routine' });
  const [generatingDoc, setGeneratingDoc] = useState(false);

  const fetchEncounters = useCallback(async () => {
    setLoading(true);
    try {
      const res = await encountersAPI.getForPatient(patientId);
      setEncounters(res.data);
    } catch (err) { console.error('Error fetching encounters:', err); }
    setLoading(false);
  }, [patientId]);

  useEffect(() => { fetchEncounters(); }, [fetchEncounters]);

  const handleSave = async () => {
    if (!formData.chief_complaint.trim()) { toast.error('Chief complaint is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        patient_id: patientId,
        diagnoses: formData.diagnoses.filter(d => d.description.trim()),
      };
      if (editingId) {
        await encountersAPI.update(editingId, payload);
        toast.success('Encounter updated!');
      } else {
        await encountersAPI.create(payload);
        toast.success('Encounter created!');
      }
      setFormOpen(false);
      setEditingId(null);
      setFormData({ ...emptyForm });
      fetchEncounters();
    } catch (err) {
      toast.error('Save failed: ' + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  const handleEdit = (enc) => {
    setFormData({
      encounter_type: enc.encounter_type || 'consultation',
      chief_complaint: enc.chief_complaint || '',
      vital_signs: enc.vital_signs || { ...emptyVitals },
      subjective: enc.subjective || '',
      objective: enc.objective || '',
      assessment: enc.assessment || '',
      plan: enc.plan || '',
      physical_exam: enc.physical_exam || '',
      diagnoses: enc.diagnoses?.length ? enc.diagnoses : [{ code: '', description: '', type: 'primary' }],
      management_plan: enc.management_plan || '',
      follow_up: enc.follow_up || '',
      status: enc.status || 'completed',
    });
    setEditingId(enc.id);
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await encountersAPI.delete(id);
      toast.success('Encounter deleted');
      fetchEncounters();
    } catch { toast.error('Delete failed'); }
  };

  const handleNewEncounter = () => {
    setFormData({ ...emptyForm });
    setEditingId(null);
    setFormOpen(true);
  };

  const openDocGeneration = (enc) => {
    setDocEncounter(enc);
    setDocType('medical_report');
    setDocFields({ additional_notes: '', start_date: '', end_date: '', certificate_reason: '', referred_to: '', specialty: '', urgency: 'routine' });
    setDocDialogOpen(true);
  };

  const handleGenerateDocument = async () => {
    if (!docEncounter) return;
    setGeneratingDoc(true);
    try {
      const res = await encountersAPI.generateDocument({
        encounter_id: docEncounter.id,
        document_type: docType,
        ...docFields,
      });
      const data = res.data;
      if (docType === 'medical_report') {
        generateEncounterReport(data);
      } else if (docType === 'medical_certificate') {
        generateMedicalCertificate(data);
      } else if (docType === 'referral_letter') {
        generateReferralLetter(data);
      }
      toast.success('Document generated and downloaded!');
      setDocDialogOpen(false);
    } catch (err) {
      toast.error('Document generation failed: ' + (err.response?.data?.detail || err.message));
    }
    setGeneratingDoc(false);
  };

  const updateVital = (field, value) => {
    const vs = { ...formData.vital_signs, [field]: value };
    if (vs.weight && vs.height) {
      const w = parseFloat(vs.weight);
      const h = parseFloat(vs.height) / 100;
      if (w > 0 && h > 0) vs.bmi = (w / (h * h)).toFixed(1);
    }
    setFormData({ ...formData, vital_signs: vs });
  };

  const addDiagnosis = () => setFormData({ ...formData, diagnoses: [...formData.diagnoses, { code: '', description: '', type: 'secondary' }] });
  const removeDiagnosis = (i) => setFormData({ ...formData, diagnoses: formData.diagnoses.filter((_, idx) => idx !== i) });
  const updateDiagnosis = (i, field, value) => {
    const d = [...formData.diagnoses];
    d[i] = { ...d[i], [field]: value };
    setFormData({ ...formData, diagnoses: d });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /><span className="ml-2 text-slate-500">Loading encounters...</span></div>;
  }

  return (
    <div className="space-y-4" data-testid="patient-encounters-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button onClick={handleNewEncounter} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="new-encounter-btn">
          <Plus className="h-4 w-4 mr-1" /> New Encounter
        </Button>
        <span className="text-sm text-slate-500">{encounters.length} encounter{encounters.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Encounter Timeline */}
      {encounters.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <Stethoscope className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium text-lg">No encounters yet</p>
          <p className="text-sm mt-1">Create a new encounter to start documenting consultations</p>
        </div>
      ) : (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-slate-200" />
          <div className="space-y-4">
            {encounters.map((enc) => (
              <EncounterCard
                key={enc.id}
                encounter={enc}
                expanded={expandedId === enc.id}
                onToggle={() => setExpandedId(expandedId === enc.id ? null : enc.id)}
                onEdit={() => handleEdit(enc)}
                onDelete={() => handleDelete(enc.id)}
                onGenerateDoc={() => openDocGeneration(enc)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ===== ENCOUNTER FORM DIALOG ===== */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white" data-testid="encounter-form-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Stethoscope className="h-5 w-5 text-emerald-600" /> {editingId ? 'Edit Encounter' : 'New Clinical Encounter'}</DialogTitle>
            <DialogDescription>Document the patient consultation with structured clinical fields.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            {/* Type & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Encounter Type *</Label>
                <Select value={formData.encounter_type} onValueChange={v => setFormData({ ...formData, encounter_type: v })}>
                  <SelectTrigger className="mt-1 rounded-xl" data-testid="encounter-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {ENCOUNTER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status</Label>
                <Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="signed">Signed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Chief Complaint */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Chief Complaint *</Label>
              <Input placeholder="Primary reason for visit..." value={formData.chief_complaint} onChange={e => setFormData({ ...formData, chief_complaint: e.target.value })} className="mt-1 rounded-xl" data-testid="chief-complaint-input" />
            </div>

            {/* Vital Signs */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-2"><Heart className="h-3.5 w-3.5" /> Vital Signs</Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                <div>
                  <Label className="text-xs text-slate-400">BP Systolic</Label>
                  <Input placeholder="120" value={formData.vital_signs.blood_pressure_systolic} onChange={e => updateVital('blood_pressure_systolic', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" data-testid="vital-bp-sys" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">BP Diastolic</Label>
                  <Input placeholder="80" value={formData.vital_signs.blood_pressure_diastolic} onChange={e => updateVital('blood_pressure_diastolic', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" data-testid="vital-bp-dia" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">HR (bpm)</Label>
                  <Input placeholder="72" value={formData.vital_signs.heart_rate} onChange={e => updateVital('heart_rate', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" data-testid="vital-hr" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Temp (C)</Label>
                  <Input placeholder="37.0" value={formData.vital_signs.temperature} onChange={e => updateVital('temperature', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" data-testid="vital-temp" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">RR (/min)</Label>
                  <Input placeholder="16" value={formData.vital_signs.respiratory_rate} onChange={e => updateVital('respiratory_rate', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">SpO2 (%)</Label>
                  <Input placeholder="98" value={formData.vital_signs.oxygen_saturation} onChange={e => updateVital('oxygen_saturation', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" data-testid="vital-spo2" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Weight (kg)</Label>
                  <Input placeholder="70" value={formData.vital_signs.weight} onChange={e => updateVital('weight', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Height (cm)</Label>
                  <Input placeholder="170" value={formData.vital_signs.height} onChange={e => updateVital('height', e.target.value)} className="mt-0.5 h-8 text-sm rounded-lg" />
                </div>
                {formData.vital_signs.bmi && (
                  <div>
                    <Label className="text-xs text-slate-400">BMI</Label>
                    <div className="mt-0.5 h-8 flex items-center text-sm font-medium text-emerald-700 bg-emerald-50 rounded-lg px-2">{formData.vital_signs.bmi}</div>
                  </div>
                )}
              </div>
            </div>

            {/* SOAP Notes */}
            <div className="space-y-3">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1"><ClipboardList className="h-3.5 w-3.5" /> SOAP Notes</Label>
              <div>
                <Label className="text-xs text-slate-500 font-medium">Subjective</Label>
                <Textarea placeholder="Patient's complaints, history of present illness..." value={formData.subjective} onChange={e => setFormData({ ...formData, subjective: e.target.value })} className="mt-0.5 min-h-[80px] rounded-xl" data-testid="soap-subjective" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 font-medium">Objective</Label>
                <Textarea placeholder="Physical exam findings, observations..." value={formData.objective} onChange={e => setFormData({ ...formData, objective: e.target.value })} className="mt-0.5 min-h-[80px] rounded-xl" data-testid="soap-objective" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 font-medium">Assessment</Label>
                <Textarea placeholder="Clinical impression, diagnosis..." value={formData.assessment} onChange={e => setFormData({ ...formData, assessment: e.target.value })} className="mt-0.5 min-h-[80px] rounded-xl" data-testid="soap-assessment" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 font-medium">Plan</Label>
                <Textarea placeholder="Treatment plan, medications, follow-up..." value={formData.plan} onChange={e => setFormData({ ...formData, plan: e.target.value })} className="mt-0.5 min-h-[80px] rounded-xl" data-testid="soap-plan" />
              </div>
            </div>

            {/* Physical Exam */}
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Physical Examination</Label>
              <Textarea placeholder="Detailed physical examination findings..." value={formData.physical_exam} onChange={e => setFormData({ ...formData, physical_exam: e.target.value })} className="mt-1 min-h-[80px] rounded-xl" data-testid="physical-exam" />
            </div>

            {/* Diagnoses */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Diagnoses</Label>
                <Button variant="ghost" size="sm" onClick={addDiagnosis} className="text-xs text-emerald-600 hover:text-emerald-700 h-7"><Plus className="h-3 w-3 mr-1" /> Add</Button>
              </div>
              <div className="space-y-2">
                {formData.diagnoses.map((d, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Input placeholder="ICD-10" value={d.code} onChange={e => updateDiagnosis(i, 'code', e.target.value)} className="col-span-2 h-8 text-xs rounded-lg" data-testid={`diagnosis-code-${i}`} />
                    <Input placeholder="Diagnosis description" value={d.description} onChange={e => updateDiagnosis(i, 'description', e.target.value)} className="col-span-7 h-8 text-xs rounded-lg" data-testid={`diagnosis-desc-${i}`} />
                    <Select value={d.type} onValueChange={v => updateDiagnosis(i, 'type', v)}>
                      <SelectTrigger className="col-span-2 h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="primary">Primary</SelectItem><SelectItem value="secondary">Secondary</SelectItem></SelectContent>
                    </Select>
                    {i > 0 && <button onClick={() => removeDiagnosis(i)} className="col-span-1 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>}
                  </div>
                ))}
              </div>
            </div>

            {/* Management Plan & Follow-up */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Management Plan</Label>
                <Textarea placeholder="Treatment approach, medications, referrals..." value={formData.management_plan} onChange={e => setFormData({ ...formData, management_plan: e.target.value })} className="mt-1 min-h-[70px] rounded-xl" data-testid="management-plan" />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Follow-up</Label>
                <Textarea placeholder="Follow-up instructions, timeline..." value={formData.follow_up} onChange={e => setFormData({ ...formData, follow_up: e.target.value })} className="mt-1 min-h-[70px] rounded-xl" data-testid="follow-up" />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end pt-2 border-t">
              <Button variant="outline" onClick={() => setFormOpen(false)} className="rounded-xl">Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="save-encounter-btn">
                {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving...</> : <><Stethoscope className="h-4 w-4 mr-1" /> {editingId ? 'Update' : 'Save'} Encounter</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ===== DOCUMENT GENERATION DIALOG ===== */}
      <Dialog open={docDialogOpen} onOpenChange={setDocDialogOpen}>
        <DialogContent className="max-w-md bg-white" data-testid="doc-gen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-emerald-600" /> Generate Document</DialogTitle>
            <DialogDescription>Create a clinical document from this encounter.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Document Type</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger className="mt-1 rounded-xl" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="medical_report">Medical Report</SelectItem>
                  <SelectItem value="medical_certificate">Medical Certificate</SelectItem>
                  <SelectItem value="referral_letter">Referral Letter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {docType === 'medical_certificate' && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Reason</Label>
                  <Input placeholder="e.g., Medical leave due to acute illness" value={docFields.certificate_reason} onChange={e => setDocFields({ ...docFields, certificate_reason: e.target.value })} className="mt-1 rounded-xl" data-testid="cert-reason" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Start Date</Label>
                    <Input type="date" value={docFields.start_date} onChange={e => setDocFields({ ...docFields, start_date: e.target.value })} className="mt-1 rounded-xl" data-testid="cert-start" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">End Date</Label>
                    <Input type="date" value={docFields.end_date} onChange={e => setDocFields({ ...docFields, end_date: e.target.value })} className="mt-1 rounded-xl" data-testid="cert-end" />
                  </div>
                </div>
              </>
            )}

            {docType === 'referral_letter' && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">Referred To</Label>
                  <Input placeholder="Doctor or facility name" value={docFields.referred_to} onChange={e => setDocFields({ ...docFields, referred_to: e.target.value })} className="mt-1 rounded-xl" data-testid="ref-to" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Specialty</Label>
                    <Input placeholder="e.g., Cardiology" value={docFields.specialty} onChange={e => setDocFields({ ...docFields, specialty: e.target.value })} className="mt-1 rounded-xl" data-testid="ref-specialty" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Urgency</Label>
                    <Select value={docFields.urgency} onValueChange={v => setDocFields({ ...docFields, urgency: v })}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white"><SelectItem value="routine">Routine</SelectItem><SelectItem value="urgent">Urgent</SelectItem><SelectItem value="emergency">Emergency</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            <div>
              <Label className="text-xs text-slate-500">Additional Notes</Label>
              <Textarea placeholder="Optional notes..." value={docFields.additional_notes} onChange={e => setDocFields({ ...docFields, additional_notes: e.target.value })} className="mt-1 min-h-[60px] rounded-xl" />
            </div>

            <Button onClick={handleGenerateDocument} disabled={generatingDoc} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="generate-doc-btn">
              {generatingDoc ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating...</> : <><Download className="h-4 w-4 mr-1" /> Generate & Download PDF</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- ENCOUNTER CARD ---
function EncounterCard({ encounter, expanded, onToggle, onEdit, onDelete, onGenerateDoc }) {
  const vs = encounter.vital_signs || {};
  const hasVitals = vs.blood_pressure_systolic || vs.heart_rate || vs.temperature;
  const typeLabel = ENCOUNTER_TYPES.find(t => t.value === encounter.encounter_type)?.label || encounter.encounter_type;

  return (
    <div className="relative pl-12" data-testid={`encounter-card-${encounter.id}`}>
      {/* Timeline dot */}
      <div className="absolute left-4 top-4 w-5 h-5 rounded-full bg-emerald-500 border-3 border-white shadow-sm z-10 flex items-center justify-center">
        <Stethoscope className="h-3 w-3 text-white" />
      </div>

      <div className={`border rounded-xl overflow-hidden transition-all ${expanded ? 'border-emerald-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}>
        {/* Header */}
        <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-slate-50 transition-colors text-left">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm text-slate-800">{encounter.chief_complaint || 'Encounter'}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ENCOUNTER_TYPE_COLORS[encounter.encounter_type] || 'bg-slate-100 text-slate-600'}`}>{typeLabel}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[encounter.status] || ''}`}>{encounter.status}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{formatDate(encounter.encounter_date)}</span>
                <span className="flex items-center gap-1"><User className="h-3 w-3" />{encounter.provider_name}</span>
                {encounter.diagnoses?.length > 0 && encounter.diagnoses[0].description && (
                  <span className="flex items-center gap-1"><AlertTriangle className="h-3 w-3" />{encounter.diagnoses[0].code ? `${encounter.diagnoses[0].code} - ` : ''}{encounter.diagnoses[0].description}</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {hasVitals && (
              <div className="hidden md:flex items-center gap-2 mr-3 text-xs text-slate-500">
                {vs.blood_pressure_systolic && <span className="flex items-center gap-0.5"><Activity className="h-3 w-3" />{vs.blood_pressure_systolic}/{vs.blood_pressure_diastolic}</span>}
                {vs.heart_rate && <span className="flex items-center gap-0.5"><Heart className="h-3 w-3" />{vs.heart_rate}</span>}
                {vs.temperature && <span className="flex items-center gap-0.5"><Thermometer className="h-3 w-3" />{vs.temperature}C</span>}
              </div>
            )}
            {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          </div>
        </button>

        {/* Expanded content */}
        {expanded && (
          <div className="border-t border-slate-100 px-4 py-4 space-y-4 bg-slate-50/50">
            {/* Vitals grid */}
            {hasVitals && (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {vs.blood_pressure_systolic && <VitalBadge icon={<Activity className="h-3.5 w-3.5" />} label="BP" value={`${vs.blood_pressure_systolic}/${vs.blood_pressure_diastolic} mmHg`} />}
                {vs.heart_rate && <VitalBadge icon={<Heart className="h-3.5 w-3.5" />} label="HR" value={`${vs.heart_rate} bpm`} />}
                {vs.temperature && <VitalBadge icon={<Thermometer className="h-3.5 w-3.5" />} label="Temp" value={`${vs.temperature} C`} />}
                {vs.respiratory_rate && <VitalBadge icon={<Activity className="h-3.5 w-3.5" />} label="RR" value={`${vs.respiratory_rate} /min`} />}
                {vs.oxygen_saturation && <VitalBadge icon={<Activity className="h-3.5 w-3.5" />} label="SpO2" value={`${vs.oxygen_saturation}%`} />}
                {vs.weight && <VitalBadge icon={<Activity className="h-3.5 w-3.5" />} label="Weight" value={`${vs.weight} kg`} />}
                {vs.height && <VitalBadge icon={<Activity className="h-3.5 w-3.5" />} label="Height" value={`${vs.height} cm`} />}
                {vs.bmi && <VitalBadge icon={<Activity className="h-3.5 w-3.5" />} label="BMI" value={vs.bmi} />}
              </div>
            )}

            {/* SOAP sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {encounter.subjective && <SoapSection title="Subjective" text={encounter.subjective} />}
              {encounter.objective && <SoapSection title="Objective" text={encounter.objective} />}
              {encounter.assessment && <SoapSection title="Assessment" text={encounter.assessment} />}
              {encounter.plan && <SoapSection title="Plan" text={encounter.plan} />}
            </div>

            {encounter.physical_exam && <SoapSection title="Physical Examination" text={encounter.physical_exam} />}

            {/* Diagnoses */}
            {encounter.diagnoses?.length > 0 && encounter.diagnoses[0].description && (
              <div>
                <h4 className="text-xs font-semibold uppercase text-slate-500 mb-1">Diagnoses</h4>
                <div className="space-y-1">
                  {encounter.diagnoses.filter(d => d.description).map((d, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${d.type === 'primary' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>{d.type}</span>
                      {d.code && <span className="font-mono text-xs text-slate-500">{d.code}</span>}
                      <span className="text-slate-700">{d.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {encounter.management_plan && <SoapSection title="Management Plan" text={encounter.management_plan} />}
            {encounter.follow_up && <SoapSection title="Follow-up" text={encounter.follow_up} />}

            {/* Actions */}
            <div className="flex gap-2 pt-2 border-t border-slate-200">
              <Button variant="outline" size="sm" onClick={onGenerateDoc} className="rounded-lg text-xs" data-testid={`gen-doc-${encounter.id}`}>
                <FileText className="h-3.5 w-3.5 mr-1" /> Generate Document
              </Button>
              <Button variant="outline" size="sm" onClick={onEdit} className="rounded-lg text-xs" data-testid={`edit-enc-${encounter.id}`}>
                <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
              <Button variant="outline" size="sm" onClick={onDelete} className="rounded-lg text-xs text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-enc-${encounter.id}`}>
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function VitalBadge({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-100">
      <span className="text-emerald-600">{icon}</span>
      <div><p className="text-[10px] text-slate-400 uppercase">{label}</p><p className="text-sm font-semibold text-slate-700">{value}</p></div>
    </div>
  );
}

function SoapSection({ title, text }) {
  return (
    <div className="bg-white rounded-lg px-3 py-2 border border-slate-100">
      <h4 className="text-xs font-semibold uppercase text-slate-400 mb-1">{title}</h4>
      <p className="text-sm text-slate-700 whitespace-pre-wrap">{text}</p>
    </div>
  );
}
